import axios from "../utils/index";

export type OperationalAiFocus = Record<string, string>;

export type OperationalAiClientContext = {
  surface?: string;
  route?: string;
  pageType?: string;
  timezone?: string;
  locale?: string;
  focus?: OperationalAiFocus;
};

export type OperationalAiTurn = {
  role: "user" | "assistant";
  content: string;
};

export type OperationalAiToolResult = {
  toolName: string;
  success: boolean;
  outcome?: string;
  summary?: string;
  data?: Record<string, unknown>;
  confidence?: number;
  errorCode?: string;
  errorMessage?: string;
};

export type OperationalAiProposal = {
  proposalId: string;
  actionType: string;
  riskLevel?: string;
  status?: string;
  previewJson?: string;
  expiresAt?: number;
  resultJson?: string;
  failureCode?: string;
};

export type OperationalAiUiBlock = {
  type: string;
  id?: string;
  title?: string;
  severity?: string;
  props?: Record<string, unknown>;
};

export type OperationalAiDashboardState = {
  selectedHospitalId?: string;
  selectedShiftId?: string;
  selectedDoctorId?: string;
  selectedState?: string;
  composition?: "default" | "ai" | string;
  mode?: string;
  title?: string;
  layout?: string;
  mapLayer?: string;
  mapView?: string;
  showMap?: boolean;
  accountType?: string;
};

export type OperationalAiCommandResponse = {
  operationId?: string;
  answer?: string;
  grounded?: boolean;
  deterministicFallback?: boolean;
  provider?: string;
  model?: string;
  toolResults?: OperationalAiToolResult[];
  actionProposals?: OperationalAiProposal[];
  warnings?: string[];
  blocks?: OperationalAiUiBlock[];
  dashboardState?: OperationalAiDashboardState;
  actions?: Array<Record<string, unknown>>;
  contextChanges?: Record<string, unknown>;
  dashboardChanges?: OperationalAiDashboardState & Record<string, unknown>;
  suggestedFollowUps?: string[];
  errorCode?: string;
};

export type OperationalAiDashboardLocation = {
  hospitalId?: string;
  name?: string;
  city?: string;
  location?: string;
  hospitalImage?: string;
  hospitalImageKey?: string;
  latitude?: number;
  longitude?: number;
  unfilledShifts?: number;
  urgentShifts?: number;
  filledShifts?: number;
  unfilledWithoutApplications?: number;
  pendingApplications?: number;
  openInsights?: number;
  state?: string;
  selected?: boolean;
  network?: "hospital" | "clinics";
  brand?: "imed" | "jones";
  siteCount?: number;
  brandName?: string;
};

export type OperationalAiDashboardResponse = {
  operationId?: string;
  generatedAt?: number;
  scope?: Record<string, unknown>;
  metrics?: Record<string, number | string>;
  locations?: OperationalAiDashboardLocation[];
  priorities?: Array<Record<string, unknown>>;
  updates?: Array<Record<string, unknown>>;
  shifts?: Array<Record<string, unknown>>;
  insights?: OperationalAiInsight[];
  suggestions?: Array<Record<string, unknown>>;
  signals?: Array<Record<string, unknown>>;
  analytics?: Record<string, unknown>;
  blocks?: OperationalAiUiBlock[];
  dashboardState?: OperationalAiDashboardState;
  found?: Array<Record<string, unknown>>;
  opportunities?: Array<Record<string, unknown>>;
  cases?: OperationalCase[];
  intelligence?: Record<string, unknown>;
};

export type OperationalCaseDoctor = {
  doctorId?: string;
  doctorName?: string;
  name?: string;
  shiftId?: string;
  hospitalId?: string;
  profileImage?: string;
  specialty?: unknown;
  skillLevel?: string;
  workedBefore?: boolean;
  preCredentialed?: boolean;
  alreadyApplied?: boolean;
  alreadyInvited?: boolean;
  favourite?: boolean;
  canInvite?: boolean;
  matchReason?: string;
  subtitle?: string;
  previousWorkCount?: number;
  locationState?: string;
  locationCity?: string;
  city?: string;
};

export type OperationalCase = {
  caseId?: string;
  kind?: string;
  title?: string;
  why?: string;
  next?: string;
  priority?: string;
  hospitalId?: string;
  hospitalName?: string;
  hospitalImage?: string;
  shiftId?: string;
  shiftDayId?: string;
  date?: string;
  startTime?: number;
  endTime?: number;
  hoursUntilStart?: number;
  specialty?: string;
  skillLevel?: string;
  pendingApplications?: number;
  candidateCount?: number;
  previousWorkCount?: number;
  credentialedCount?: number;
  city?: string;
  state?: string;
  evidence?: unknown;
  doctors?: OperationalCaseDoctor[];
  actions?: Array<{ label?: string; action?: string }>;
};

export type OperationalAiInsight = {
  insightId: string;
  hospitalId?: string;
  type?: string;
  insightType?: string;
  severity?: string;
  status?: string;
  title?: string;
  body?: string;
  whyItMatters?: string;
  confidence?: number;
  urgency?: number;
  impact?: number;
  createdAt?: number;
  updatedAt?: number;
  evidence?: unknown;
  entityRefs?: unknown;
  actions?: unknown;
};

const dataOf = <T>(response: { data?: { code?: number; data?: T; message?: string } }): T => {
  if (response.data?.code === 200 && response.data.data !== undefined) {
    return response.data.data;
  }
  throw new Error(response.data?.message || "Operational intelligence request failed.");
};

const pageContext = (focus?: OperationalAiFocus): OperationalAiClientContext => {
  const next = { ...(focus || {}) };
  const pageType = next.pageType;
  delete next.pageType;
  return {
    surface: "HOSPITAL_WEB",
    route: window.location.hash.replace(/^#/, "") || window.location.pathname,
    pageType,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Australia/Sydney",
    locale: navigator.language || "en-AU",
    focus: next,
  };
};

export const runOperationalCommand = async (
  message: string,
  options?: {
    focus?: OperationalAiFocus;
    conversation?: OperationalAiTurn[];
  },
): Promise<OperationalAiCommandResponse> => {
  const response = await axios.post("/v2/hospital/ai/operations/command", {
    message,
    conversation: (options?.conversation || []).slice(-80),
    context: pageContext(options?.focus),
  });
  return dataOf<OperationalAiCommandResponse>(response);
};

export const getOperationalDashboard = async (selectedHospitalId?: string): Promise<OperationalAiDashboardResponse> => {
  const response = await axios.get("/v2/hospital/ai/operations/dashboard", {
    params: selectedHospitalId ? { selectedHospitalId } : {},
  });
  return dataOf<OperationalAiDashboardResponse>(response);
};

export const getOperationalBriefing = async (
  focus?: OperationalAiFocus,
): Promise<OperationalAiToolResult> => {
  const response = await axios.get("/v2/hospital/ai/operations/briefing", {
    params: {},
    headers: {},
  });
  void focus;
  return dataOf<OperationalAiToolResult>(response);
};

export const getOperationalInsights = async (filters?: {
  status?: string;
  insightType?: string;
}): Promise<OperationalAiInsight[]> => {
  const response = await axios.get("/v2/hospital/ai/operations/insights", {
    params: {
      status: filters?.status || undefined,
      insightType: filters?.insightType || undefined,
    },
  });
  const result = dataOf<OperationalAiToolResult>(response);
  const insights = result.data?.insights;
  return Array.isArray(insights) ? (insights as OperationalAiInsight[]) : [];
};

export const evaluateShiftOperationally = async (
  shiftId: string,
): Promise<OperationalAiToolResult> => {
  const response = await axios.post(`/v2/hospital/ai/operations/tools/evaluate_shift`, {
    context: pageContext({ shiftId }),
    arguments: { shiftId },
  });
  return dataOf<OperationalAiToolResult>(response);
};

export const getShiftApplicationsOperationally = async (
  shiftId: string,
): Promise<OperationalAiToolResult> => {
  const response = await axios.post(
    `/v2/hospital/ai/operations/tools/get_shift_applications`,
    {
      context: pageContext({ shiftId }),
      arguments: { shiftId },
    },
  );
  return dataOf<OperationalAiToolResult>(response);
};

export const findSuitableDoctorsOperationally = async (
  shiftId: string,
  limit = 5,
  focus?: OperationalAiFocus,
): Promise<OperationalAiToolResult> => {
  const response = await axios.post(
    `/v2/hospital/ai/operations/tools/find_suitable_doctors`,
    {
      context: pageContext({ ...(focus || {}), shiftId }),
      arguments: { shiftId, limit },
    },
  );
  return dataOf<OperationalAiToolResult>(response);
};

export const evaluateDoctorForShiftOperationally = async (
  shiftId: string,
  doctorId: string,
  shiftDayId?: string,
): Promise<OperationalAiToolResult> => {
  const response = await axios.post(
    `/v2/hospital/ai/operations/tools/evaluate_doctor_for_shift`,
    {
      context: pageContext({
        shiftId,
        doctorId,
        ...(shiftDayId ? { shiftDayId } : {}),
      }),
      arguments: {
        shiftId,
        doctorId,
        ...(shiftDayId ? { shiftDayId } : {}),
      },
    },
  );
  return dataOf<OperationalAiToolResult>(response);
};

export const prepareOperationalAction = async (
  actionType: string,
  payload: Record<string, unknown>,
  focus?: OperationalAiFocus,
): Promise<OperationalAiProposal> => {
  const response = await axios.post("/v2/hospital/ai/operations/actions/prepare", {
    actionType,
    payload,
    context: pageContext(focus),
  });
  return dataOf<OperationalAiProposal>(response);
};

export const confirmOperationalAction = async (
  proposalId: string,
  focus?: OperationalAiFocus,
): Promise<OperationalAiProposal> => {
  const response = await axios.post(
    `/v2/hospital/ai/operations/actions/${proposalId}/confirm`,
    { context: pageContext(focus) },
  );
  return dataOf<OperationalAiProposal>(response);
};

export const rejectOperationalAction = async (
  proposalId: string,
  focus?: OperationalAiFocus,
): Promise<OperationalAiProposal> => {
  const response = await axios.post(
    `/v2/hospital/ai/operations/actions/${proposalId}/reject`,
    { context: pageContext(focus) },
  );
  return dataOf<OperationalAiProposal>(response);
};

export const dismissOperationalInsight = async (insightId: string): Promise<boolean> => {
  const response = await axios.post(
    `/v2/hospital/ai/operations/insights/${insightId}/dismiss`,
    { context: pageContext() },
  );
  return Boolean(dataOf<boolean>(response));
};

export const acknowledgeOperationalInsight = async (insightId: string): Promise<boolean> => {
  const response = await axios.post(
    `/v2/hospital/ai/operations/insights/${insightId}/acknowledge`,
    { context: pageContext() },
  );
  return Boolean(dataOf<boolean>(response));
};

export const resolveOperationalInsight = async (insightId: string): Promise<boolean> => {
  const response = await axios.post(
    `/v2/hospital/ai/operations/insights/${insightId}/resolve`,
    { context: pageContext() },
  );
  return Boolean(dataOf<boolean>(response));
};

export const parseProposalPreview = (
  proposal?: OperationalAiProposal | null,
): Record<string, unknown> => {
  if (!proposal?.previewJson) return {};
  try {
    const parsed = JSON.parse(proposal.previewJson);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

export const openOperationalCommand = (prefill?: string) => {
  window.dispatchEvent(
    new CustomEvent("statdoctor-ai-command", { detail: { prefill } }),
  );
};

export const getDashboardPreference = async (key = "dashboard.pinnedBlocks") => {
  const response = await axios.get("/v2/hospital/ai/operations/preference", { params: { key } });
  return dataOf<{ key?: string; value?: Record<string, unknown> }>(response);
};

export const putDashboardPreference = async (
  value: Record<string, unknown>,
  key = "dashboard.pinnedBlocks",
) => {
  const response = await axios.post("/v2/hospital/ai/operations/preference", { key, value });
  return dataOf<{ key?: string; value?: Record<string, unknown> }>(response);
};
